import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import NotificationBar from "./home/NotifictionBar";
import api from "../lib/api.ts";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatPrice } from "../utils/functions";
import { getPageSettings } from "../services/pagesettingServices.ts";

export default function Buyer() {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [buyerPackages, setBuyerPackages] = useState([]);
  const [buyerStartIndex, setBuyerStartIndex] = useState(0);
  const [pageSettings, setPageSettings] = useState(null);
  const [buyerFaqs, setBuyerFaqs] = useState([]);
  const [slideDirection, setSlideDirection] = useState(0); // 0: no animation, 1: right, -1: left
  const cardsPerPage = 4;

  // Package request states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRequestingPackage, setIsRequestingPackage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // const lang = navigator.language.startsWith("ar") ? "ar" : "en";
        const response = await api.get(`/buyerpackages`);
        setBuyerPackages(response.data); // <-- set API data here
      } catch (err) {
        console.error("❌ Error fetching slider data:", err);

        // Set fallback slide
      }
    };

    fetchData();
  }, []);

  // Fetch page settings API
  useEffect(() => {
    const fetchPageSettings = async () => {
      try {
        const response = await api.get("/pagesettings/clientbuyerpage", {
          params: { lang: language },
        });

        if (response.data) {
          setPageSettings(response.data);
        }
      } catch (error) {
        console.error("❌ Error fetching page settings:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
    };

    const fetchBuyerFaqs = async () => {
      try {
        const response = await getPageSettings({
          page: "buyerfaqs",
          lang: language,
        });
        setBuyerFaqs(response.buyer_faqs || []);
      } catch (error) {
        console.error("❌ Error fetching buyer FAQs:", error);
      }
    };

    fetchPageSettings();
    fetchBuyerFaqs();
  }, [language]);

  // Slide left
  const handleBuyerLeft = () => {
    setSlideDirection(-1);
    setBuyerStartIndex((prev) => Math.max(prev - cardsPerPage, 0));
  };

  // Slide right
  const handleBuyerRight = () => {
    setSlideDirection(1);
    setBuyerStartIndex((prev) =>
      Math.min(prev + cardsPerPage, buyerPackages.length - cardsPerPage)
    );
  };

  // Package request functions
  const handlePackageRequest = (pkg) => {
    if (!user) {
      showError(translate("buyerPage.messages.loginRequired"));
      navigate("/login");
      return;
    }

    setSelectedPackage(pkg);
    setReceiptImage(null);
    setImagePreview(null);
    setShowConfirmDialog(true);
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedPackage(null);
    setReceiptImage(null);
    setImagePreview(null);
  };

  // Confirm and request package
  const confirmAndRequestPackage = async () => {
    if (!selectedPackage || !user) return;

    // Validate receipt image is provided
    if (!receiptImage) {
      showError(translate("buyerPage.messages.uploadReceiptRequired"));
      return;
    }

    try {
      setIsRequestingPackage(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("buyerPackageId", selectedPackage.id);
      formData.append("userId", user.id);
      formData.append("receiptImage", receiptImage);

      const response = await api.post("/buyerpackagehistories", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        // Close dialog and show success
        closeConfirmDialog();
        showSuccess(translate("buyerPage.messages.packageRequestSuccess"));
      }
    } catch (err) {
      console.error("Error requesting package:", err);
      showError(
        err.response?.data?.message ||
          translate("buyerPage.messages.packageRequestFailed")
      );
      closeConfirmDialog();
    } finally {
      setIsRequestingPackage(false);
    }
  };

  // Map API registration steps to steps array
  const steps = pageSettings?.registration_steps?.map((step, index) => ({
    id: String(index + 1).padStart(2, "0"), // Convert to "01", "02", etc.
    title: step.title?.value || `Step ${index + 1}`,
    desc: step.description?.value || "",
    img: step.imageUrl?.value || "",
  })) || [
    // Fallback steps if API data is not available
    {
      id: "01",
      title: translate("buyerPage.steps.step1Title"),
      desc: translate("buyerPage.steps.step1Desc"),
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQCztTF_LQCrVVQWc631bre20jP1qaTOJq23w&s",
    },
    {
      id: "02",
      title: translate("buyerPage.steps.step2Title"),
      desc: translate("buyerPage.steps.step2Desc"),
      img: "https://media.istockphoto.com/id/978525302/photo/blue-car-on-laptop-keypad.jpg?s=612x612&w=0&k=20&c=1BKbAFqCeV4eLI8j1KfdoCT4jNvge8bTpodVp4iieMM=",
    },
    {
      id: "03",
      title: translate("buyerPage.steps.step3Title"),
      desc: translate("buyerPage.steps.step3Desc"),
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcThR1hagQcjW0-1wbxLiovhprx9cjdjW9l8VPy_SMoDLHeGTNCZRAhMIEDw23SR7JMKfWc&usqp=CAU",
    },
    {
      id: "04",
      title: translate("buyerPage.steps.step4Title"),
      desc: translate("buyerPage.steps.step4Desc"),
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTPdB0akyCVqlbC48jbQQSvH8jO6ldIv4lltg&s",
    },
  ];

  // Dynamically compute zig-zag SVG and step positions based on the number of steps
  const zigWidth = 198; // matches previous SVG width for consistency
  const leftX = 32; // left x for zig
  const rightX = 166; // right x for zig
  const stepSpacing = 240; // vertical space between steps
  const paddingTop = 100; // top padding before first marker
  const paddingBottom = 140; // bottom padding after last marker

  const svgHeight =
    paddingTop + paddingBottom + Math.max(0, steps.length - 1) * stepSpacing;

  const points = Array.from(
    { length: Math.max(1, steps.length) + 1 },
    (_, i) => ({
      x: i % 2 === 0 ? rightX : leftX,
      y: paddingTop + i * stepSpacing,
    })
  );

  const markerPositions = points.slice(0, Math.max(1, steps.length));

  const pathD =
    `M ${points[0].x} ${points[0].y} ` +
    points
      .slice(1)
      .map((p) => `L ${p.x} ${p.y}`)
      .join(" ");

  // Get meta fields from pageSettings
  const metaDescription =
    pageSettings?.meta_fields?.[0]?.meta_description?.value || "";
  const metaKeywords =
    pageSettings?.meta_fields?.[0]?.meta_keywords?.value || "";
  const pageTitle = pageSettings?.hero_banner?.[0]?.title?.value;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        {metaDescription && (
          <meta name='description' content={metaDescription} />
        )}
        {metaKeywords && <meta name='keywords' content={metaKeywords} />}
      </Helmet>
      <NotificationBar />
      <style>{`
        .blog-content .ql-ui { display: none; }
        .blog-content ol { list-style: decimal; margin-left: 1.25rem; }
        .blog-content ul { list-style: disc; margin-left: 1.25rem; }
        .blog-content li { margin: 0.375rem 0; }
        .blog-content li[data-list="bullet"] { list-style-type: disc; }
        .blog-content li[data-list="ordered"] { list-style-type: decimal; }
        .blog-content h1, .blog-content h2, .blog-content h3 { color: #111827; }
        .blog-content p { color: #374151; white-space: pre-wrap; }
        .blog-content a { color: #2563eb; text-decoration: underline; }
      `}</style>
      <section
        className='max-w-7xl justify-center mx-auto items-center'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Top Banner */}
        <div
          className='relative w-full h-[400px] flex flex-col items-center justify-center text-center px-4'
          style={{
            backgroundImage: pageSettings?.hero_banner?.[0]?.imageUrl?.value
              ? `url('${pageSettings.hero_banner[0].imageUrl.value}')`
              : "url('https://www.cleveland-nissan.com/static/industry-automotive/medium-images/new_keys.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className='absolute inset-0 bg-black bg-opacity-50'></div>

          <div className='relative z-10 '>
            <motion.h1
              className='mt-[40px] md:mt-[20px] text-white text-[40px] md:text-4xl font-bold mb-3'
              initial={{ y: -100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ type: "spring", stiffness: 100, damping: 10 }}
            >
              {pageSettings?.hero_banner?.[0]?.title?.value ||
                "We Help You Sell Your Car Fast And Hassle-Free."}
            </motion.h1>
            <br />
            <motion.div
              className='relative z-10 max-w-3xl mx-auto'
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <p className='text-[#F5F5F5] mb-5 text-[24px]'>
                {pageSettings?.hero_banner?.[0]?.subtitle?.value ||
                  translate("buyerPage.heroSubtitle")}
              </p>
              <br />
              <button
                onClick={() => {
                  const btnLink =
                    pageSettings?.hero_banner?.[0]?.btnLink?.value;
                  if (btnLink) {
                    // If it's a full URL, navigate to it
                    if (btnLink.startsWith("http")) {
                      window.location.href = btnLink;
                    } else {
                      // If it's a relative path, use navigate
                      navigate(btnLink);
                    }
                  }
                }}
                className='relative px-6 py-2 font-medium text-[#ffffff] bg-[#F15A29] rounded overflow-hidden group mt-4'
              >
                <span className='absolute top-0 left-0 w-0 h-[2px] bg-[#ffffff] transition-all duration-500 group-hover:w-full'></span>
                <span className='absolute bottom-0 right-0 w-0 h-[2px] bg-[white] transition-all duration-500 group-hover:w-full'></span>
                <span className='absolute top-0 right-0 h-0 w-[2px] bg-[white] transition-all duration-500 group-hover:h-full'></span>
                <span className='absolute bottom-0 left-0 h-0 w-[2px] bg-[white] transition-all duration-500 group-hover:h-full'></span>
                {pageSettings?.hero_banner?.[0]?.btnText?.value ||
                  translate("buyerPage.becomeBuyer")}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        className='max-w-7xl justify-center mx-auto items-center py-16 relative'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Heading */}
        <motion.div
          className='text-center mb-12'
          initial={{ opacity: 0, y: 50 }} // start invisible and lower
          whileInView={{ opacity: 1, y: 0 }} // fade in and move up
          viewport={{ once: false, amount: 0.3 }} // trigger once when 30% visible
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h2 className='text-3xl font-bold'>
            {translate("buyerPage.afterYouGetRegistered")
              .split(" ")
              .map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toLowerCase() === "after" ||
                    word.toLowerCase() === "بعد"
                      ? "text-[#F15A29]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h2>
          <div
            className='blog-content prose prose-lg text-gray-500 mt-2 max-w-2xl mx-auto'
            dangerouslySetInnerHTML={{
              __html:
                pageSettings?.registration_steps_header?.[0]?.description
                  ?.value ||
                translate("buyerPage.registrationStepsDescription"),
            }}
          />
        </motion.div>

        {/* Desktop Version */}
        <div className='max-w-7xl  mx-auto  items-center relative w-full justify-center hidden lg:flex'>
          <div className='relative'>
            {/* Dynamic SVG line */}
            <svg
              width={zigWidth}
              height={svgHeight}
              viewBox={`0 0 ${zigWidth} ${svgHeight}`}
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d={pathD}
                stroke='#E95923'
                strokeWidth='4'
                strokeLinecap='round'
                strokeDasharray='16 16'
              />
              {markerPositions.map((p, i) => {
                const isLeft = i % 2 !== 0;
                const bigSize = 66;
                const smallSize = 24;
                const bigX = isLeft ? 2 : zigWidth - bigSize - 2;
                const bigY = p.y - bigSize / 2;
                const smallX = bigX + (isLeft ? 21 : 21);
                const smallY = bigY + 21;
                return (
                  <g key={`marker-${i}`} opacity='1'>
                    <rect
                      x={bigX}
                      y={bigY}
                      width={bigSize}
                      height={bigSize}
                      rx={20}
                      fill='white'
                    />
                    <rect
                      x={smallX}
                      y={smallY}
                      width={smallSize}
                      height={smallSize}
                      rx={10}
                      fill='#F29B7B'
                    />
                  </g>
                );
              })}
            </svg>

            {/* Step cards */}
            {steps.map((step, idx) => {
              const isLeft = idx % 2 !== 0; // alternate side
              return (
                <motion.div
                  key={step.id}
                  className={`absolute w-[380px] bg-white rounded-xl shadow-md p-4 transform -translate-y-1/2 ${
                    isLeft ? "left-[-420px]" : "left-[220px]"
                  }`}
                  style={{ top: (markerPositions[idx]?.y || 0) - 100 }}
                  initial={{ opacity: 0, x: isLeft ? -100 : 100 }} // start left or right
                  whileInView={{ opacity: 1, x: 0 }} // move to center
                  viewport={{ once: false, amount: 0.3 }} // trigger once
                  transition={{ duration: 0.4, delay: idx * 0.2 }} // stagger cards
                >
                  <img
                    src={step.img}
                    alt={step.title}
                    className='rounded-md mb-3 w-full h-36 object-cover'
                  />
                  <h3 className='font-bold'>{step.title}</h3>
                  <p className='text-gray-600 text-sm mt-2'>{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile Version */}
        <div className='grid gap-6 px-4 block lg:hidden'>
          {steps.map((step, idx) => (
            <motion.div
              key={step.id}
              className='bg-white rounded-xl shadow-md p-4'
              initial={{ opacity: 0, x: -50 }} // start hidden, shifted left
              whileInView={{ opacity: 1, x: 0 }} // animate to visible
              viewport={{ once: true, amount: 0.2 }} // trigger when 20% in view
              transition={{ duration: 0.6, delay: idx * 0.2 }} // stagger each card
            >
              <img
                src={step.img}
                alt={step.title}
                className='rounded-md mb-3 w-full h-40 object-cover'
              />

              <h3 className='font-bold'>{step.title}</h3>
              <p className='text-gray-600 text-sm mt-2'>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section
        className='py-12 bg-white'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        <div className='text-center mb-10'>
          <motion.h2
            className='text-3xl font-bold'
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6 }}
          >
            {translate("buyerPage.choosePlan")
              .split(" ")
              .map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toLowerCase() === "choose" ||
                    word.toLowerCase() === "اختر"
                      ? "text-[#F15A29]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </motion.h2>
          <motion.p
            className='text-gray-500 mt-2'
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {translate("buyerPage.choosePlanSubtitle")}
          </motion.p>
        </div>

        <motion.div
          className='grid p-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto'
          key={buyerStartIndex} // Force re-render on index change
          initial={{
            opacity: 0,
            x: slideDirection === 1 ? 100 : slideDirection === -1 ? -100 : 0,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            duration: 0.5,
            ease: "easeInOut",
          }}
          onAnimationComplete={() => setSlideDirection(0)}
        >
          {buyerPackages
            .slice(buyerStartIndex, buyerStartIndex + cardsPerPage)
            .map((pkg, i) => (
              <motion.div
                key={pkg.idx}
                className='flex flex-col items-center'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                {/* Card Content */}
                <div className='border shadow-md p-6 w-full rounded-lg rounded-b-3xl relative z-10 bg-gray-50'>
                  <h3 className='font-semibold text-lg mb-2'>{pkg.name}</h3>
                  <div className='flex items-center gap-1'>
                    <div className='flex items-center gap-1'>
                      <svg
                        width='30'
                        height='30'
                        viewBox='0 0 30 30'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M25 14.6175L24.848 14.4763C24.6023 14.2409 24.3099 14.1232 23.9941 14.1232H22.3567C22.3801 14.4057 22.3918 14.6881 22.3918 14.9941C22.3918 15.3001 22.3801 15.5825 22.3567 15.8768H23.4678C24.3099 15.8768 25 16.677 25 17.6774V18.1246L24.848 17.9716C24.6023 17.748 24.3099 17.6303 23.9941 17.6303H22.1111C21.2105 21.5963 18.0643 23.75 13.1053 23.75H6.74269C6.74269 23.75 7.6082 23.0792 7.6082 20.8314V17.6303H6.54387C5.69006 17.6303 5 16.8183 5 15.8297V15.3825L5.16375 15.5237C5.39766 15.7473 5.69007 15.8768 6.00586 15.8768H7.6082V14.1232H6.54387C5.69006 14.1232 5 13.3112 5 12.3226V11.8754L5.16375 12.0284C5.39766 12.252 5.69007 12.3697 6.00586 12.3697H7.6082V9.29808C7.6082 6.97966 6.74269 6.25 6.74269 6.25H13.1053C17.924 6.25 21.152 8.38013 22.0994 12.3697H23.4678C24.3099 12.3697 25 13.17 25 14.1703V14.6175ZM12.8713 7.12088H10.2164V12.3697H19.1403C18.5322 8.72142 16.462 7.12088 12.8713 7.12088ZM19.3509 14.9941C19.3509 14.6881 19.3392 14.4057 19.3275 14.1232H10.2164V15.8768H19.3275C19.3392 15.5825 19.3509 15.3001 19.3509 14.9941ZM10.2164 22.8673H12.8947C16.6959 22.7732 18.5672 20.9373 19.1403 17.6303H10.2164V22.8673Z'
                          fill='#E95923'
                        />
                      </svg>

                      <p className='text-2xl font-bold'>{pkg.deposit}</p>
                    </div>
                  </div>

                  <hr className='my-3 border-[#cecece]' />
                  <br />

                  <h4
                    className={`font-semibold text-lg mb-2 ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {translate("buyerPage.features.heading")}
                  </h4>
                  <ul
                    className={`space-y-2 text-gray-700 ${
                      isRTL ? "space-y-reverse" : ""
                    }`}
                  >
                    <li
                      className={`flex items-center gap-2 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 14 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='flex-shrink-0'
                      >
                        <path
                          d='M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z'
                          fill='#2BC92E'
                        />
                      </svg>
                      <span>
                        {translate("buyerPage.features.bidLimit")}{" "}
                        {pkg.creditLimit}
                      </span>
                    </li>

                    <li
                      className={`flex items-center gap-2 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 14 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='flex-shrink-0'
                      >
                        <path
                          d='M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z'
                          fill='#2BC92E'
                        />
                      </svg>
                      {translate("buyerPage.features.freeParkingDays")}{" "}
                      {pkg.freeParkingDays}
                    </li>
                    <li
                      className={`flex items-center gap-2 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 14 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='flex-shrink-0'
                      >
                        <path
                          d='M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z'
                          fill='#2BC92E'
                        />
                      </svg>
                      {translate("buyerPage.features.fineAfterFreeParking")}{" "}
                      {pkg.fineAfterFreeParkingDays}
                    </li>
                  </ul>
                </div>

                {/* Button Overlapped */}
                <button
                  onClick={() => handlePackageRequest(pkg)}
                  disabled={isRequestingPackage}
                  className='mt-[-20px] pb-4 w-full pt-9 bg-black text-white py-2 rounded-b-lg hover:bg-[#f15a29] transition z-0 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <p className='leading-2'>
                    {isRequestingPackage
                      ? translate("buyerPage.buttons.requesting")
                      : translate("buyerPage.buttons.requestPackage")}
                  </p>
                </button>
              </motion.div>
            ))}
        </motion.div>

        {/* Navigation buttons at extreme right */}
        <div
          className={`w-full flex pr-6 gap-2 ${
            isRTL ? "justify-start flex-row-reverse" : "justify-end"
          }`}
        >
          {/* Left button */}
          <button
            className={`transition-transform p-3 rounded-lg flex items-center justify-center w-12 h-12 ${
              buyerStartIndex === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-[#d1d1d16c] text-white cursor-pointer hover:bg-[#cacaca9c] hover:scale-105"
            }`}
            onClick={handleBuyerLeft}
            disabled={buyerStartIndex === 0}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 10 18'
              fill='none'
            >
              <path
                d='M0.210874 8.60638L7.58135 0.979093C7.64983 0.90821 7.7316 0.851501 7.82198 0.812248C7.91237 0.772995 8.00961 0.75193 8.10813 0.750243C8.20667 0.748556 8.30456 0.766281 8.39625 0.802417C8.48792 0.838553 8.57158 0.892429 8.64245 0.960926C8.71331 1.02932 8.77 1.11117 8.80926 1.20151C8.84852 1.29195 8.8696 1.3891 8.87129 1.48769C8.87297 1.58617 8.85524 1.68409 8.81909 1.77582C8.78294 1.86745 8.72909 1.95109 8.66061 2.02197L1.81062 9.10967L8.89921 15.9587C9.04233 16.097 9.12465 16.2865 9.12805 16.4855C9.13146 16.6845 9.05568 16.8767 8.91738 17.0198C8.77908 17.1629 8.58959 17.2452 8.39059 17.2486C8.1916 17.252 7.9994 17.1763 7.85628 17.0379L0.229039 9.66742C0.158058 9.59903 0.101349 9.51719 0.062097 9.42684C0.0227431 9.33641 0.00167579 9.23915 -1.0317e-05 9.14067C-0.00169814 9.04208 0.0160278 8.94416 0.0522645 8.85253C0.0883996 8.7608 0.142276 8.67716 0.210874 8.60638Z'
                fill='#131313'
              />
            </svg>
          </button>

          {/* Right button */}
          <button
            className={`transition-transform p-3 rounded-lg flex items-center justify-center w-12 h-12 ${
              buyerStartIndex + cardsPerPage >= buyerPackages.length
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-[#d1d1d16c] text-white cursor-pointer hover:bg-[#cacaca9c] hover:scale-105"
            }`}
            onClick={handleBuyerRight}
            disabled={buyerStartIndex + cardsPerPage >= buyerPackages.length}
          >
            <svg
              width='10'
              height='18'
              viewBox='0 0 10 18'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M9.03104 9.53104L1.53104 17.031C1.46136 17.1007 1.37863 17.156 1.28759 17.1937C1.19654 17.2314 1.09896 17.2508 1.00042 17.2508C0.901871 17.2508 0.804289 17.2314 0.713245 17.1937C0.6222 17.156 0.539474 17.1007 0.469792 17.031C0.400109 16.9614 0.344834 16.8786 0.307122 16.7876C0.26941 16.6965 0.25 16.599 0.25 16.5004C0.25 16.4019 0.26941 16.3043 0.307122 16.2132C0.344834 16.1222 0.400109 16.0395 0.469792 15.9698L7.4401 9.00042L0.469792 2.03104C0.329061 1.89031 0.25 1.69944 0.25 1.50042C0.25 1.30139 0.329061 1.11052 0.469792 0.969792C0.610522 0.829062 0.801394 0.75 1.00042 0.75C1.19944 0.75 1.39031 0.829062 1.53104 0.969792L9.03104 8.46979C9.10077 8.53945 9.15609 8.62216 9.19384 8.71321C9.23158 8.80426 9.25101 8.90186 9.25101 9.00042C9.25101 9.09898 9.23158 9.19657 9.19384 9.28762C9.15609 9.37867 9.10077 9.46139 9.03104 9.53104Z'
                fill='#131313'
              />
            </svg>
          </button>
        </div>
      </section>

      <br />
      <section
        className='max-w-7xl justify-center mx-auto items-center p-6'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Heading */}
        <div className='text-center mb-6'>
          <h2 className='text-[32px] font-bold'>
            {translate("buyerPage.faq.heading")
              .split(" ")
              .map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toLowerCase() === "frequently" ||
                    word.toLowerCase() === "الأسئلة"
                      ? "text-[#F15A29]"
                      : ""
                  }
                >
                  {word}{" "}
                </span>
              ))}
          </h2>
          <p className='text-gray-600 text-[16px] mt-1'>
            {translate("buyerPage.faq.subtitle")}
          </p>
        </div>

        {/* FAQ List */}
        {buyerFaqs.map((item, index) => (
          <div
            key={index}
            className='bg-white border border-gray-300 shadow-md rounded-md mb-4 p-4 transition hover:shadow-lg'
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className={`w-full flex justify-between items-center font-semibold ${
                isRTL ? "text-right flex-row-reverse" : "text-left"
              }`}
            >
              {item.question?.value}
              <span
                className={`transition-transform duration-300 ${
                  openIndex === index ? "rotate-180" : "rotate-0"
                }`}
              >
                <svg
                  width='18'
                  height='10'
                  viewBox='0 0 18 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M16.8975 1.39714L9.39751 8.89714C9.29204 9.00248 9.14907 9.06165 9.00001 9.06165C8.85094 9.06165 8.70798 9.00248 8.60251 8.89714L1.10251 1.39714C1.00315 1.29051 0.949055 1.14948 0.951626 1.00375C0.954197 0.858024 1.01323 0.718985 1.11629 0.615925C1.21935 0.512865 1.35839 0.453831 1.50411 0.45126C1.64984 0.448688 1.79088 0.502781 1.89751 0.602141L9.00001 7.7037L16.1025 0.602141C16.2091 0.502781 16.3502 0.448688 16.4959 0.45126C16.6416 0.453831 16.7807 0.512865 16.8837 0.615925C16.9868 0.718985 17.0458 0.858024 17.0484 1.00375C17.051 1.14948 16.9969 1.29051 16.8975 1.39714Z'
                    fill='#131313'
                  />
                </svg>
              </span>
            </button>
            {openIndex === index && item.answer && (
              <div
                className='pt-2 text-gray-600'
                dangerouslySetInnerHTML={{ __html: item.answer?.value || "" }}
              />
            )}
          </div>
        ))}
      </section>

      {/* Package Request Confirmation Dialog */}
      {showConfirmDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto'>
            {/* Header */}
            <div
              className={`flex justify-between items-center p-6 border-b border-gray-200 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <h3 className='text-xl font-bold text-gray-800'>
                {translate("buyerPackages.modal.requestPackageConfirmation")}
              </h3>
              <button
                onClick={closeConfirmDialog}
                className='text-gray-400 hover:text-gray-600 transition-colors'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Main Content - Two Column Layout */}
            <div className='p-6'>
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${
                  isRTL ? "lg:grid-flow-col-dense" : ""
                }`}
                dir={isRTL ? "rtl" : "ltr"}
                lang={language}
              >
                {/* Left Side - Important Notice */}
                <div
                  className={`space-y-6 ${
                    isRTL ? "lg:order-2 space-y-reverse" : "lg:order-1"
                  }`}
                >
                  <div className='bg-red-50 border border-red-200 rounded-lg p-6'>
                    <div className='flex items-center mb-4'>
                      <div className='flex items-center space-x-2'>
                        <svg
                          className='w-6 h-6 text-red-600'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                        <svg
                          className='w-6 h-6 text-yellow-500'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                      <h4
                        className={`text-lg font-bold text-gray-800 ${
                          isRTL ? "mr-2" : "ml-2"
                        }`}
                      >
                        {translate("buyerPackages.modal.notice")}
                      </h4>
                    </div>

                    <p
                      className={`text-gray-700 mb-4 font-medium ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("buyerPackages.modal.noticeMessage")}
                    </p>

                    <div
                      className={`space-y-2 mb-4 ${
                        isRTL ? "space-y-reverse" : ""
                      }`}
                    >
                      <div
                        className={`flex items-start ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <span
                          className={`text-red-600 ${isRTL ? "ml-2" : "mr-2"}`}
                        >
                          •
                        </span>
                        <span
                          className={`text-red-700 text-sm ${
                            isRTL ? "text-right" : "text-left"
                          }`}
                        >
                          {translate(
                            "buyerPackages.modal.packageWillBeActivated"
                          )}
                        </span>
                      </div>

                      <div
                        className={`flex items-start ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <span
                          className={`text-red-600 ${isRTL ? "ml-2" : "mr-2"}`}
                        >
                          •
                        </span>
                        <span
                          className={`text-red-700 text-sm ${
                            isRTL ? "text-right" : "text-left"
                          }`}
                        >
                          {translate(
                            "buyerPackages.modal.uploadPaymentReceipt"
                          )}
                        </span>
                      </div>
                      <div
                        className={`flex items-start ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                      >
                        <span
                          className={`text-red-600 ${isRTL ? "ml-2" : "mr-2"}`}
                        >
                          •
                        </span>
                        <span
                          className={`text-red-700 text-sm ${
                            isRTL ? "text-right" : "text-left"
                          }`}
                        >
                          {translate("buyerPackages.modal.waitForApproval")}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`bg-red-100 border border-red-300 rounded-md p-3 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      <p className='text-red-800 text-sm font-medium'>
                        {translate("buyerPackages.modal.ensureDepositPayment")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Package Details and Actions */}
                <div
                  className={`space-y-6 ${
                    isRTL ? "lg:order-1 space-y-reverse" : "lg:order-2"
                  }`}
                >
                  {/* Package Details */}
                  <div>
                    <h4
                      className={`text-lg font-semibold text-gray-800 mb-4 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("buyerPackages.modal.packageDetails")}
                    </h4>
                    <div
                      className={`bg-gray-50 rounded-lg p-4 space-y-3 ${
                        isRTL ? "space-y-reverse text-right" : "text-left"
                      }`}
                    >
                      <div>
                        <span className='text-sm text-gray-600'>
                          {translate("buyerPackages.labels.packageName")}:
                        </span>
                        <p className='font-semibold text-gray-800'>
                          {selectedPackage?.name}
                        </p>
                      </div>
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <span className='text-sm text-gray-600'>
                            {translate("buyerPackages.labels.depositRequired")}:
                          </span>
                          <p className='font-semibold text-gray-800'>
                            {formatPrice(selectedPackage?.deposit) || "-"}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm text-gray-600'>
                            {translate("buyerPackages.labels.biddingLimit")}:
                          </span>
                          <p className='font-semibold text-gray-800'>
                            {formatPrice(selectedPackage?.creditLimit) || "-"}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm text-gray-600'>
                            {translate("buyerPackages.labels.freeParking")}:
                          </span>
                          <p className='font-semibold text-gray-800'>
                            {selectedPackage?.freeParkingDays || "-"}{" "}
                            {translate("buyerPackages.labels.days")}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm text-gray-600'>
                            {translate("buyerPackages.labels.carLimit")}:
                          </span>
                          <p className='font-semibold text-gray-800'>
                            {selectedPackage?.carLimit || "-"}{" "}
                            {translate("buyerPackages.labels.cars")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Receipt Upload */}
                  <div>
                    <label
                      className={`block text-sm font-medium text-gray-700 mb-2 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("buyerPackages.modal.paymentReceiptImage")}{" "}
                      <span className='text-red-500'>
                        {translate("buyerPackages.modal.required")}
                      </span>
                    </label>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={handleImageChange}
                      className={`block w-full text-sm text-gray-500 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F15A29] file:text-white hover:file:bg-[#E54A1A] ${
                        isRTL ? "file:ml-4" : "file:mr-4"
                      }`}
                      required
                    />
                    <p
                      className={`text-xs text-gray-500 mt-1 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("buyerPackages.modal.uploadReceiptMessage")}
                    </p>

                    {/* Image Preview */}
                    {imagePreview && (
                      <div className='mt-3'>
                        <label
                          className={`block text-sm font-medium text-gray-700 mb-2 ${
                            isRTL ? "text-right" : "text-left"
                          }`}
                        >
                          {translate("buyerPackages.modal.imagePreview")}
                        </label>
                        <div className='text-center'>
                          <img
                            src={imagePreview}
                            alt='Receipt preview'
                            className='mx-auto max-h-32 max-w-full rounded-lg border border-gray-300'
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirmation Checkbox */}
                  <div
                    className={`flex items-start pt-4 ${
                      isRTL
                        ? "flex-row-reverse space-x-reverse space-x-3"
                        : "space-x-3"
                    }`}
                  >
                    <input
                      type='checkbox'
                      id='confirmRequest'
                      checked={receiptImage !== null}
                      onChange={() => {}} // Controlled by file upload
                      className={`mt-1 h-4 w-4 text-[#F15A29] focus:ring-[#F15A29] border-gray-300 rounded ${
                        isRTL ? "ml-3" : ""
                      }`}
                    />
                    <label
                      htmlFor='confirmRequest'
                      className={`text-sm text-gray-700 ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {translate("buyerPackages.modal.confirmationCheckbox")}
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div
                    className={`flex pt-4 ${
                      isRTL
                        ? "flex-row-reverse space-x-reverse space-x-3"
                        : "space-x-3"
                    }`}
                  >
                    <button
                      onClick={closeConfirmDialog}
                      disabled={isRequestingPackage}
                      className='flex-1 px-6 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium'
                    >
                      {translate("buyerPackages.modal.cancel")}
                    </button>
                    <button
                      onClick={confirmAndRequestPackage}
                      disabled={isRequestingPackage || !receiptImage}
                      className='flex-1 px-6 py-3 bg-[#F15A29] text-white rounded-lg hover:bg-[#E54A1A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium'
                    >
                      {isRequestingPackage
                        ? translate("buyerPage.buttons.requesting")
                        : translate("buyerPackages.modal.confirmRequest")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
